import { getStratoLogger } from '../../../core/sdk/sdk-logger.js';
import { routesTo } from '../../validation/message-routing.js';
import {
  ValidationError,
  type ValidationErrorSource,
  type ValidationState,
  type ValidationTrigger,
  type ValidityStateKey,
} from '../../validation/types.js';
import { FormValidationError } from '../form-error-classes.js';
import type {
  ControlRegistrationHandle,
  FormApi,
  FormSnapshot,
  FormStatus,
  FormValidator,
  RegisteredControl,
} from '../form.types.js';

/**
 * Mutable config the `<Form>` component feeds into the runtime on every render.
 * @internal
 */
export interface FormRuntimeConfig<TValues = Record<string, unknown>> {
  formId?: string;
  validators: FormValidator<TValues>[];
  onSubmit?: (context: {
    snapshot: FormSnapshot<TValues>;
    form: FormApi<TValues>;
  }) => void;
  onInvalid?: (context: {
    snapshot: FormSnapshot<TValues>;
    status: FormStatus;
    form: FormApi<TValues>;
  }) => void;
  onValidityChange?: (state: ValidationState) => void;
}

/**
 * The full runtime surface: the public {@link FormApi} plus the internal hooks
 * the `<Form>` component and registered controls use.
 * @internal
 */
export interface FormRuntime<TValues = Record<string, unknown>>
  extends FormApi<TValues> {
  /** Registers a control and returns its imperative handle. */
  register: (control: RegisteredControl) => ControlRegistrationHandle;
  /** Subscribes to any runtime change (for `useSyncExternalStore`). */
  subscribe: (listener: () => void) => () => void;
  /** The form-wide errors currently rendered by `<FormMessages>`. */
  getFormMessages: () => ValidationError[];
  /** Runtime-owned field errors rendered by `FormFieldMessages`. */
  getFieldMessages: (controlId: string) => ValidationError[];
  /**
   * Replaces the config getter that the runtime calls on each submit/validate.
   * Called by `<Form>` when an external runtime is supplied via the `form` prop.
   * Pass `undefined` to revert to the no-op default (e.g. on unmount).
   * @internal
   */
  _bindConfig?: (fn: (() => FormRuntimeConfig<TValues>) | undefined) => void;
}

type RegisteredEntry = {
  control: RegisteredControl;
  token: symbol;
};

// Ordinal comparator for building canonical, environment-independent dedup and
// signature keys. `localeCompare` is locale/ICU-dependent, so it could order
// non-ASCII ids differently across machines — these keys must be deterministic.
function compareOrdinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function errorIdentity(error: ValidationError): string {
  const target = Array.isArray(error.formControlId)
    ? [...error.formControlId].sort(compareOrdinal).join(',')
    : error.formControlId ?? '';
  const formId = error instanceof FormValidationError ? error.formId : '';
  return `${error.validityStateKey.toString()}|${target}|${formId}`;
}

function errorSignature(error: ValidationError): string {
  return `${errorIdentity(error)}|${error.message}`;
}

function errorSetSignature(errors: ValidationError[]): string {
  return errors.map(errorSignature).sort(compareOrdinal).join('\u0000');
}

function stateIsPending(state: ValidationState): boolean {
  return state.status === 'pending';
}

const SOURCE_PRIORITY: Record<ValidationErrorSource, number> = {
  custom: 0,
  strato: 1,
  native: 2,
};

export function createFormRuntime<TValues = Record<string, unknown>>(
  getConfig?: () => FormRuntimeConfig<TValues>,
): FormRuntime<TValues> {
  const config: FormRuntimeConfig<TValues> = { validators: [] };
  const controls = new Map<string, RegisteredEntry>();
  const fieldStates = new Map<string, ValidationState>();
  let manualErrors: ValidationError[] = [];
  let formValidatorErrors: ValidationError[] = [];
  let isSubmitted = false;
  let activeValidationRuns = 0;
  let validationRunId = 0;

  let readConfig = getConfig ?? (() => config);
  const listeners = new Set<() => void>();
  const warnedDuplicateNames = new Set<string>();

  let formMessagesCache: ValidationError[] = [];
  let formMessagesSignature = '';
  const fieldMessagesCache = new Map<
    string,
    { signature: string; messages: ValidationError[] }
  >();
  let lastValiditySignature: string | undefined;

  function notify() {
    for (const listener of listeners) {
      listener();
    }
  }

  function hasPendingFields(): boolean {
    return [...fieldStates.values()].some(stateIsPending);
  }

  function isPending(): boolean {
    return activeValidationRuns > 0 || hasPendingFields();
  }

  function aggregateErrors(): ValidationError[] {
    const seen = new Set<string>();
    const result: ValidationError[] = [];
    const push = (error: ValidationError) => {
      const signature = errorSignature(error);
      if (!seen.has(signature)) {
        seen.add(signature);
        result.push(error);
      }
    };
    for (const state of fieldStates.values()) {
      state.errors.forEach(push);
    }
    formValidatorErrors.forEach(push);
    manualErrors.forEach(push);
    return result;
  }

  function errorsForField(controlId: string): ValidationError[] {
    const matched = aggregateErrors().filter(
      (error) => matchesTarget(error, controlId) && routesTo(error, 'field'),
    );
    // So a consumer that renders only errors[0] gets a deterministic result.
    // Sort is stable, so two errors of the same source keep their relative order.
    return [...matched].sort(
      (a, b) => SOURCE_PRIORITY[a.source] - SOURCE_PRIORITY[b.source],
    );
  }

  function buildStatus(): FormStatus {
    const errors = aggregateErrors();
    return {
      isValid: errors.length === 0,
      isPending: isPending(),
      isSubmitted,
      errors,
    };
  }

  function buildSnapshot(): FormSnapshot<TValues> {
    const values: Record<string, unknown> = {};
    const fields: FormSnapshot['fields'] = {};
    for (const { control } of controls.values()) {
      const key = control.name ?? control.id;
      if (Object.hasOwn(values, key) && !warnedDuplicateNames.has(key)) {
        warnedDuplicateNames.add(key);
        getStratoLogger('Form').warn(
          `Multiple form controls share the name "${key}"; only the last value is kept in the form snapshot.`,
        );
      }
      values[key] = control.getValue();
      const fieldErrors = errorsForField(control.id);
      fields[control.id] = {
        isValid: fieldErrors.length === 0,
        isTouched: fieldStates.has(control.id),
        errors: fieldErrors,
      };
    }
    return {
      values: values as TValues,
      fields,
    };
  }

  function emitValidityChange(trigger: ValidationTrigger) {
    const errors = aggregateErrors();
    const signature = `${errors.length === 0 ? 'valid' : 'invalid'}|${
      isPending() ? 'pending' : 'done'
    }|${errorSetSignature(errors)}`;
    // The runtime notifies liberally — it emits on the way into and out of every
    // validation run and on every mutation. Collapse to a signature so
    // `onValidityChange` fires only on real transitions, not identical recomputations.
    if (signature === lastValiditySignature) {
      return;
    }
    lastValiditySignature = signature;
    readConfig().onValidityChange?.({
      status: isPending() ? 'pending' : 'done',
      trigger,
      errors,
    });
  }

  async function runValidation(
    trigger: ValidationTrigger,
    reveal: boolean,
  ): Promise<FormStatus> {
    const runId = ++validationRunId;
    const runControls = [...controls.values()];
    activeValidationRuns += 1;
    emitValidityChange(trigger);
    notify();

    try {
      const results = await Promise.all(
        runControls.map(async ({ control, token }) => {
          const state = await control.validateField({ reveal, trigger });
          return { id: control.id, token, state };
        }),
      );

      if (runId === validationRunId) {
        for (const { id, token, state } of results) {
          if (controls.get(id)?.token === token) {
            fieldStates.set(id, state);
          }
        }
      }

      const snapshot = buildSnapshot();
      const validatorResults = await Promise.all(
        readConfig().validators.map((validator) =>
          runFormValidator(validator, snapshot.values, readConfig().formId),
        ),
      );

      if (runId === validationRunId) {
        formValidatorErrors = validatorResults
          .flat()
          .map((error) =>
            error.source === 'custom' ? error : cloneWithCustomSource(error),
          );
      }
    } finally {
      activeValidationRuns -= 1;
      emitValidityChange(trigger);
      notify();
    }

    return buildStatus();
  }

  const api: FormRuntime<TValues> = {
    getStatus: buildStatus,
    getSnapshot: buildSnapshot,

    validate: () => runValidation('manual', true),

    submit: async () => {
      isSubmitted = true;
      // Consumer-set errors (`setError`) persist across submits; only the
      // consumer clears them via `clearError` / `resetValidation`. This keeps a
      // server-side error visible on retry until the consumer removes it.
      const status = await runValidation('submit', true);

      if (!status.isValid) {
        focusFirstInvalid(controls, status.errors);
        readConfig().onInvalid?.({
          snapshot: buildSnapshot(),
          status,
          form: api,
        });
      } else {
        readConfig().onSubmit?.({ snapshot: buildSnapshot(), form: api });
      }
      notify();
      return buildStatus();
    },

    resetValidation: () => {
      validationRunId += 1;
      activeValidationRuns = 0;
      manualErrors = [];
      formValidatorErrors = [];
      isSubmitted = false;
      fieldStates.clear();
      for (const { control } of controls.values()) {
        control.resetValidation?.();
      }
      lastValiditySignature = undefined;
      emitValidityChange('manual');
      notify();
    },

    setError: (error) => {
      const identity = errorIdentity(error);
      manualErrors = [
        ...manualErrors.filter(
          (existing) => errorIdentity(existing) !== identity,
        ),
        error,
      ];
      emitValidityChange('manual');
      notify();
    },

    clearError: (target, validityStateKey) => {
      manualErrors = manualErrors.filter(
        (error) => !matchesTarget(error, target, validityStateKey),
      );
      emitValidityChange('manual');
      notify();
    },

    register: (control) => {
      // Per-registration token. A control id can be re-registered (remount, or a
      // duplicate id). Every handle captures its token and no-ops if the live entry's
      // token differs, so a stale handle's async report/unregister can't clobber the
      // current registration.
      const token = Symbol(control.id);
      controls.set(control.id, { control, token });
      notify();
      return {
        reportValidity: (state) => {
          if (controls.get(control.id)?.token !== token) {
            return;
          }
          fieldStates.set(control.id, state);
          emitValidityChange(state.trigger);
          notify();
        },
        unregister: () => {
          if (controls.get(control.id)?.token !== token) {
            return;
          }
          controls.delete(control.id);
          fieldStates.delete(control.id);
          emitValidityChange('manual');
          notify();
        },
      };
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    // `FormMessages`/`FormFieldMessages` read these via `useSyncExternalStore`, which
    // compares snapshots by reference. Return the cached array unless the error set's
    // signature actually changed — a fresh array on every read forces a re-render and
    // can loop.
    getFormMessages: () => {
      // Form-wide errors: the routing subclass, plus any control's native/Strato
      // error explicitly routed to the form outlet via `_messageTarget`.
      const current = aggregateErrors().filter(
        (error) =>
          error instanceof FormValidationError || routesTo(error, 'form'),
      );
      const signature = errorSetSignature(current);
      if (signature !== formMessagesSignature) {
        formMessagesSignature = signature;
        formMessagesCache = current;
      }
      return formMessagesCache;
    },

    getFieldMessages: (controlId) => {
      const current = errorsForField(controlId);
      const signature = errorSetSignature(current);
      const cached = fieldMessagesCache.get(controlId);
      if (cached?.signature === signature) {
        return cached.messages;
      }
      fieldMessagesCache.set(controlId, { signature, messages: current });
      return current;
    },

    _bindConfig: (fn) => {
      readConfig = fn ?? (() => config);
    },
  };

  return api;
}

async function runFormValidator<TValues>(
  validator: FormValidator<TValues>,
  values: TValues,
  formId: string | undefined,
): Promise<ValidationError[]> {
  try {
    return await validator({ values });
  } catch (cause) {
    if (formId !== undefined) {
      return [
        new FormValidationError('Validation failed.', {
          formId,
          cause,
        }),
      ];
    }
    return [
      new ValidationError('Validation failed.', { source: 'custom', cause }),
    ];
  }
}

function cloneWithCustomSource(error: ValidationError): ValidationError {
  if (error instanceof FormValidationError) {
    return new FormValidationError(error.message, {
      formId: error.formId,
      validityStateKey: error.validityStateKey,
      formControlId: error.formControlId,
      cause: error.cause,
    });
  }
  return new ValidationError(error.message, {
    source: 'custom',
    validityStateKey: error.validityStateKey,
    formControlId: error.formControlId,
    cause: error.cause,
  });
}

function matchesTarget(
  error: ValidationError,
  target: string,
  validityStateKey?: ValidityStateKey,
): boolean {
  const routedTo =
    error instanceof FormValidationError
      ? error.formId === target
      : Array.isArray(error.formControlId)
      ? error.formControlId.includes(target)
      : error.formControlId === target;
  if (!routedTo) {
    return false;
  }
  return (
    validityStateKey === undefined ||
    error.validityStateKey.equals(validityStateKey)
  );
}

function focusFirstInvalid(
  controls: Map<string, RegisteredEntry>,
  errors: ValidationError[],
) {
  const invalidIds = new Set(
    errors.flatMap((error) =>
      error.formControlId === undefined
        ? []
        : Array.isArray(error.formControlId)
        ? error.formControlId
        : [error.formControlId],
    ),
  );
  for (const { control } of controls.values()) {
    if (invalidIds.has(control.id)) {
      control.focus();
      return;
    }
  }
}
